import {
	ActionIcon,
	Button,
	Group,
	Stack,
	Switch,
	Table,
	Text,
	Textarea,
	Tooltip,
} from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import {
	type AssistantStyleText,
	fillStyleText,
} from "@/locales/assistant-style";
import { type ExtraRuleDto, STYLE_LIMITS } from "@/types/ai-assistant-style";

type RuleField = "larangan" | "pengganti";

interface Props {
	t: AssistantStyleText;
	name: string;
	rules: ExtraRuleDto[];
	/** Pesan error per `extraRules.<i>.<field>` dan `extraRules`. */
	errorFor: (key: string) => string | undefined;
	disabled: boolean;
	onChange: (rules: ExtraRuleDto[]) => void;
}

const MAX_CHARS = STYLE_LIMITS.extraRuleCharsMax;

/** Tabel larangan tambahan: Larangan / Gantinya lakukan / aktif / hapus. */
export function StyleRulesTable({
	t,
	name,
	rules,
	errorFor,
	disabled,
	onChange,
}: Props) {
	const update = (index: number, patch: Partial<ExtraRuleDto>) =>
		onChange(rules.map((r, i) => (i === index ? { ...r, ...patch } : r)));
	const remove = (index: number) =>
		onChange(rules.filter((_, i) => i !== index));
	const add = () =>
		onChange([...rules, { larangan: "", pengganti: "", aktif: true }]);
	const full = rules.length >= STYLE_LIMITS.extraRulesMax;

	const cell = (rule: ExtraRuleDto, index: number, field: RuleField) => (
		<Textarea
			aria-label={field === "larangan" ? t.rules.larangan : t.rules.pengganti}
			autosize
			minRows={1}
			maxRows={4}
			maxLength={MAX_CHARS}
			value={rule[field]}
			onChange={(e) => update(index, { [field]: e.currentTarget.value })}
			description={fillStyleText(t.rules.counter, {
				n: rule[field].length,
				max: MAX_CHARS,
			})}
			inputWrapperOrder={["input", "description", "error"]}
			error={errorFor(`extraRules.${index}.${field}`)}
			disabled={disabled}
			data-rule-field={`${index}.${field}`}
		/>
	);

	return (
		<Stack gap={6}>
			<Text fw={600} size="sm">
				{t.rules.title}
			</Text>
			<Text size="xs" c="dimmed">
				{fillStyleText(t.rules.description, { name })}
			</Text>
			{rules.length === 0 ? (
				<Text size="sm" c="dimmed" data-style-rules-empty>
					{t.rules.empty}
				</Text>
			) : (
				<Table.ScrollContainer minWidth={560}>
					<Table verticalSpacing="xs" data-style-rules>
						<Table.Thead>
							<Table.Tr>
								<Table.Th>{t.rules.larangan}</Table.Th>
								<Table.Th>{t.rules.pengganti}</Table.Th>
								<Table.Th w={70}>{t.rules.aktif}</Table.Th>
								<Table.Th w={50} />
							</Table.Tr>
						</Table.Thead>
						<Table.Tbody>
							{rules.map((rule, index) => (
								// biome-ignore lint/suspicious/noArrayIndexKey: baris belum punya id; indeks dipakai juga sebagai kunci error validasi
								<Table.Tr key={index} data-style-rule={index}>
									<Table.Td valign="top">
										{cell(rule, index, "larangan")}
									</Table.Td>
									<Table.Td valign="top">
										{cell(rule, index, "pengganti")}
									</Table.Td>
									<Table.Td valign="top" pt={14}>
										<Switch
											aria-label={t.rules.aktif}
											checked={rule.aktif}
											onChange={(e) =>
												update(index, { aktif: e.currentTarget.checked })
											}
											disabled={disabled}
											data-rule-active={index}
										/>
									</Table.Td>
									<Table.Td valign="top" pt={10}>
										<Tooltip label={t.rules.remove}>
											<ActionIcon
												variant="subtle"
												color="red"
												aria-label={t.rules.remove}
												onClick={() => remove(index)}
												disabled={disabled}
												data-rule-remove={index}
											>
												<IconTrash size={16} />
											</ActionIcon>
										</Tooltip>
									</Table.Td>
								</Table.Tr>
							))}
						</Table.Tbody>
					</Table>
				</Table.ScrollContainer>
			)}
			{errorFor("extraRules") && (
				<Text size="xs" c="red">
					{errorFor("extraRules")}
				</Text>
			)}
			<Group gap="xs">
				<Button
					variant="light"
					size="xs"
					leftSection={<IconPlus size={14} />}
					onClick={add}
					disabled={disabled || full}
					data-style-rule-add
				>
					{t.rules.add}
				</Button>
				<Text size="xs" c="dimmed">
					{rules.length}/{STYLE_LIMITS.extraRulesMax}
				</Text>
			</Group>
		</Stack>
	);
}
